// src/pages/shared/TextFieldDropdown.jsx
import React from "react";
import Sheet from "@/sot/primitives/Sheet.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";

const DROPDOWN_ICON = "/icons/appointments/dashboard/expand_more.svg";

export default function TextFieldDropdown({
  id,
  name,
  label,
  value,
  placeholder,
  options = [],
  onChange,
  required = false,
  tone = "surface",
  className = "",
  variant = "textField",
  triggerClassName = "",
  indicatorSrc = DROPDOWN_ICON,
  mobileSheet = false,
  mobileSheetTitle,
  mobileSheetCloseLabel = "Close",
}) {
  const [open, setOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches);
  const useMobileSheet = mobileSheet && isMobile;
  const rootRef = React.useRef(null);
  const selectedOption = options.find((option) => option.value === value);
  const displayValue = selectedOption?.label || value || "";
  const optionsId = `${id || name || "text-field-dropdown"}-options`;

  React.useEffect(() => {
    if (!open) return undefined;

    function onPointerDown(event) {
      if (!useMobileSheet && !rootRef.current?.contains(event.target)) setOpen(false);
    }

    function onKeyDown(event) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, useMobileSheet]);

  React.useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const sync = () => setIsMobile(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  function selectValue(nextValue) {
    onChange?.({
      target: {
        name,
        value: nextValue,
        type: "select-one",
      },
    });
    setOpen(false);
  }

  if (variant === "availability") {
    return (
      <div ref={rootRef} className={["relative space-y-0", className].join(" ").trim()}>
        {label ? (
          <label htmlFor={id} className="block text-title-small text-on-surface">
            {label}
          </label>
        ) : null}
        <button
          id={id}
          type="button"
          className={[
            "mt-2 flex h-12 w-full items-center justify-between rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.55)] bg-[rgb(var(--clr-surface))] px-4 py-3 pr-5 text-left text-body-medium text-on-surface shadow-[var(--elev-1)] transition focus:outline-none focus:ring-2 focus:ring-[rgba(29,78,216,0.14)]",
            triggerClassName,
          ].join(" ")}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={optionsId}
          onClick={() => setOpen((current) => !current)}
        >
          <span className={displayValue ? "truncate" : "truncate text-on-surface-var"}>
            {displayValue || placeholder}
          </span>
          <img
            src={indicatorSrc}
            alt=""
            className={[
              "ml-3 h-[18px] w-[18px] shrink-0 transition-transform",
              open ? "rotate-180" : "",
            ].join(" ")}
          />
        </button>

        <select
          name={name}
          value={value || ""}
          onChange={() => {}}
          required={required}
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {open && !useMobileSheet ? (
          <div
            id={optionsId}
            role="listbox"
            className="absolute left-0 top-full z-[90] mt-2 w-full overflow-hidden rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.7)] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[var(--elev-4)]"
          >
            <div className="py-1">
              {options.map((option) => {
                const selected = option.value === value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={[
                      "block w-full px-4 py-3 text-left text-body-medium transition",
                      selected
                        ? "bg-[var(--state-primary-12)] text-[rgb(var(--afrimed-primary))]"
                        : "text-on-surface hover:bg-[rgba(var(--clr-on-surface),0.04)]",
                    ].join(" ")}
                    onClick={() => selectValue(option.value)}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
        {open && useMobileSheet ? (
          <Sheet open onClose={() => setOpen(false)} ariaLabel={mobileSheetTitle || label || "Options"}>
            <div className="text-field-dropdown-mobile-sheet">
              <div className="text-field-dropdown-mobile-sheet__header"><h2>{mobileSheetTitle || label}</h2><button type="button" onClick={() => setOpen(false)} aria-label={mobileSheetCloseLabel}>×</button></div>
              <div id={optionsId} role="listbox" className="text-field-dropdown-mobile-sheet__options">
                {options.map((option) => <button key={option.value} type="button" role="option" aria-selected={option.value === value} onClick={() => selectValue(option.value)}>{option.label}</button>)}
              </div>
            </div>
          </Sheet>
        ) : null}
      </div>
    );
  }

  return (
    <div ref={rootRef} className={["relative", className].join(" ").trim()}>
      <button
        type="button"
        className="block w-full text-left"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={optionsId}
        onClick={() => setOpen((current) => !current)}
      >
        <div className="pointer-events-none">
          <TextField56
            id={id}
            name={name}
            label={label}
            value={displayValue}
            placeholder={placeholder}
            readOnly
            required={required}
            tone={tone}
          />
          <img
            src={indicatorSrc}
            alt=""
            className={[
              "pointer-events-none absolute right-5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 transition-transform",
              open ? "rotate-180" : "",
            ].join(" ")}
          />
        </div>
      </button>

      <select
        name={name}
        value={value || ""}
        onChange={() => {}}
        required={required}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {open && !useMobileSheet ? (
        <div
          id={optionsId}
          role="listbox"
          className="absolute left-0 top-full z-[90] mt-2 w-full overflow-hidden rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.7)] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[var(--elev-4)]"
        >
          <div className="py-1">
            {options.map((option) => {
              const selected = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={[
                    "block w-full px-4 py-3 text-left text-body-medium transition",
                    selected
                      ? "bg-[var(--state-primary-12)] text-[rgb(var(--afrimed-primary))]"
                      : "text-on-surface hover:bg-[rgba(var(--clr-on-surface),0.04)]",
                  ].join(" ")}
                  onClick={() => selectValue(option.value)}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
      {open && useMobileSheet ? (
        <Sheet open onClose={() => setOpen(false)} ariaLabel={mobileSheetTitle || label || "Options"}>
          <div className="text-field-dropdown-mobile-sheet">
            <div className="text-field-dropdown-mobile-sheet__header"><h2>{mobileSheetTitle || label}</h2><button type="button" onClick={() => setOpen(false)} aria-label={mobileSheetCloseLabel}>×</button></div>
            <div id={optionsId} role="listbox" className="text-field-dropdown-mobile-sheet__options">
              {options.map((option) => <button key={option.value} type="button" role="option" aria-selected={option.value === value} onClick={() => selectValue(option.value)}>{option.label}</button>)}
            </div>
          </div>
        </Sheet>
      ) : null}
    </div>
  );
}
